import React from 'react';

/**
 * Recursively extract the plain-text content of react-markdown children.
 *
 * react-markdown hands a table cell's children as an ARRAY of React nodes the
 * moment the cell contains any inline formatting (bold, code, an entity chip,
 * a link). A naive `children.join('')` then yields '[object Object]', so the
 * severity / IP / time detection below silently fails on formatted cells — and
 * Cade's evidence tables routinely use `**Critical**` / `` `10.0.0.5` ``.
 * Walking the tree for text fixes that.
 */
export function nodeText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (React.isValidElement(node)) {
    return nodeText((node.props as { children?: React.ReactNode }).children);
  }
  return '';
}

const SEVERITY_RE = /^(critical|high|medium|low|info|success)$/;
const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}(:\d+)?$/;
const TIME_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;

/**
 * Evidence-table cell renderer shared by MessageList + StreamingMessage:
 *   - a severity word  → colored severity bar + mono-uppercase label
 *   - an IP / time     → mono tabular cell (keeps inline children, e.g. chips)
 *   - everything else  → plain cell
 *
 * Detection reads the cell's extracted TEXT (via nodeText) so it fires even when
 * the cell holds inline markdown that arrives as React elements, not strings.
 */
export function EvidenceTd({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) {
  const text = nodeText(children).trim();
  const sev = text.toLowerCase();
  if (SEVERITY_RE.test(sev)) {
    return (
      <td {...props}>
        <span className="sev-cell"><span className={`sev-bar ${sev}`} /><span className={`sev-lab ${sev}`}>{text}</span></span>
      </td>
    );
  }
  if (IPV4_RE.test(text) || TIME_RE.test(text)) {
    return <td className="mono" {...props}>{children}</td>;
  }
  return <td {...props}>{children}</td>;
}
